/*
	bf_hitmap.ts
  Copyright (c) 2013-present Human Cube Inc.

  Map force position functionality.
  Works for both in-browser and node-based since it uses an injected function for obtaining the
  hitmap bitmap when needed.

	Notes:
		- x and y positions are in the range [0 to 8191] (except hex maps have a little extra x range).
    - Lazy loads the hitmaps and lazy generates zone position spots.
    - setHitmapBitmapGetterFunction(handle) must be called before using any other functions.

  TODO:
    - Generates a new hitmap if a new mapID is detected, this might need to be enhanced or we need
      to make the mapID change if the map is modified in some way (due to cards or what have you).
      Also the hitmapCache accumulates, maybe there is no benefit of keeping more than the current
      map if in a browser, but services currently benefit from this, it will be needed.
*/

import BF from './bfconst1';
import { Game } from './gameTypes';


type GetHitmapBitmapCallback = (mapID: number) => any;

var getHitmapBitmap: GetHitmapBitmapCallback | undefined = undefined;

export function setHitmapBitmapGetterFunction (getHitmapBitmapFunction: GetHitmapBitmapCallback) {
  // Sets the function handle that is called to fetch hitmap bitmaps for bitmap maps.
  // Expected call format of function handle is getHitmapBitmap(mapID: number): bitmap | null;
  getHitmapBitmap = getHitmapBitmapFunction;
}

const WORLD_WIDTH = BF.WORLD_WIDTH;    // Range of world coordinates, 0 to 8191.
const WORLD_HEIGHT = BF.WORLD_HEIGHT;  // Range of world coordinates, 0 to 8191.
const CENTER_POS = Math.floor(WORLD_WIDTH / 2);
const COORDINATE_MASK = WORLD_WIDTH - 1;  // 8191

const MAX_POSITION_SPOTS = 100;  // Max position spots generated and stored for each zone.
                                 // Stored as a single integer (y * WORLD_WIDTH + x).
                                 // Used when using a bitmap hitmap.

interface Hitmap {
  xs: number;
  ys: number;
  spot: number[][];  // Array of spots for each zone.
  bitmap?: any;
} 
interface Hitmaps {
  [Key: number]: Hitmap;
}
const hitmap: Hitmaps = {};  // not really a hitmap, more a coordinate list per map per zone.


export function confirmOrFixXY (game: Game, zoneID: number, x: number, y: number) {
  // Return the same X, Y back if valid, or returns new valid X and Y.
  // Returns defaults of CENTER_POS for x and y on failure.

  if(!x) {
    x = 0;
  }
  if(!y) {
    y = 0;
  }

  x &= COORDINATE_MASK;
  y &= COORDINATE_MASK;

  if( isZoneXY(game, zoneID, x, y) ) {
    return {x: x, y: y};
  }

  const newXY = getNewZoneXY(game, zoneID);
  return {x: newXY.x, y: newXY.y};
}

export function getNewZoneXY (game: Game, zoneID: number):
    {result: number, x: number, y: number} {
  // Where result is 0 on success, non-0 on error.
  // On any error, default values of CENTER_POS (center of map) will be returned for x and y.

  if( zoneID < 1 || zoneID > game.zones ) {
    return {result: 1, x: CENTER_POS, y: CENTER_POS};  // Error, return center.
  }

  if( game.mapFlags & BF.MAP_FLAG_BITMAP ) {
    createHitmapForBitmapMap(game);
    if( hitmap[game.mapID].spot && hitmap[game.mapID].spot[zoneID] &&
        hitmap[game.mapID].spot[zoneID].length > 0 ) {
      const p = hitmap[game.mapID].spot[zoneID][Math.floor(Math.random() *
                hitmap[game.mapID].spot[zoneID].length)];
      return {
        result: 0,
        x: Math.floor(p % WORLD_WIDTH),
        y: Math.floor(p / WORLD_WIDTH)
      };
    }
    return {result: 2, x: CENTER_POS, y: CENTER_POS};  // Error, return center.
  }

  if( game.mapFlags & BF.MAP_FLAG_HEXMAP )
      return getNewZoneXYForHexmap(game, zoneID);

  if(game.mapFlags & BF.MAP_FLAG_RAWMAP) {
      createHitmapForRawmapMap(game);
      if( hitmap[game.mapID].spot && hitmap[game.mapID].spot[zoneID] &&
              hitmap[game.mapID].spot[zoneID].length > 0 ) {
          let p = hitmap[game.mapID].spot[zoneID][Math.floor(Math.random() *
                                                  hitmap[game.mapID].spot[zoneID].length)];
          return {result: 0,
                  x: Math.floor(p % WORLD_WIDTH),
                  y: Math.floor(p / WORLD_WIDTH)
                 };
      }
      return {result: 2, x: CENTER_POS, y: CENTER_POS};  // Error, return center.
  }

  return {result: -1, x: CENTER_POS, y: CENTER_POS};
}

export function isZoneXY (
  game: Game, zoneID: number, x: number, y: number,
): number {
  // x and y are in the range [0 to WORLD_WIDTH]
  // Returns 1 if X Y are in the zone, else 0 if out of zone or error.
  if(zoneID < 1 || zoneID > game.zones) {
    return 0;
  }
  if(zoneIDFromXY(game, x, y) === zoneID) {
    return 1;
  }
  return 0;
}


export function zoneIDFromXY (game: Game, x: number, y: number): number {
  // x and y are in the range [0 to WORLD_WIDTH] (except hex maps have a little extra x range)
  // Returns zoneID or 0 for none.

  x = Math.floor(x);
  y = Math.floor(y);

  if( game.mapFlags & BF.MAP_FLAG_BITMAP ) {
    if(x < 0 || x >= WORLD_WIDTH || y < 0 || y >= WORLD_WIDTH) {
      return 0;
    }
    createHitmapForBitmapMap(game);
    if(hitmap[game.mapID].xs && hitmap[game.mapID].ys && hitmap[game.mapID].bitmap) {
      let bx = Math.floor(x / WORLD_WIDTH * hitmap[game.mapID].xs);
      if( bx >= hitmap[game.mapID].xs ) {
        bx = hitmap[game.mapID].xs - 1;
      }

      let by = Math.floor(y / WORLD_WIDTH * hitmap[game.mapID].ys);
      if( by >= hitmap[game.mapID].ys ) {
        by = hitmap[game.mapID].ys - 1;
      }

      const color = hitmap[game.mapID].bitmap[by * hitmap[game.mapID].ys + bx];
      const index = game.zoneKey.indexOf(color);
      if(index > 0) {
        return index;
      }
    }
    return 0;
  }

  if( game.mapFlags & BF.MAP_FLAG_HEXMAP ) {
    // Use the offset squares representation of hexes (as that is how it currently rendered).
  	let hx = Math.floor(x / WORLD_WIDTH * game.mapXS * 2);
  	let hy = Math.floor(y / WORLD_HEIGHT * game.mapYS);

    if((hy & 1) === 0) {
      hx--;
    }
    if(hx < 0 || hy < 0) {
      return 0;
    }
    hx = Math.floor(hx / 2);
    if(hx >= game.mapXS || hy >= game.mapYS) {
      return 0;
    }

    const mi = (hy * game.mapXS + hx) * 2;
    const z = parseInt(game.mapData.slice(mi, mi + 2), 16);
    if(z && z > 0 && z <= game.zones) {
        return z;
    }
    return 0;
    // return zoneIDAtHexmapXY(game, x, y);  // Closer to true hexes, but not right for our render.
  }

  if( game.mapFlags & BF.MAP_FLAG_RAWMAP ) {
    if(x < 0 || x >= WORLD_WIDTH || y < 0 || y >= WORLD_WIDTH) {
      return 0;
    }
  	x = Math.floor(x / WORLD_WIDTH * game.mapXS);
  	y = Math.floor(y / WORLD_HEIGHT * game.mapYS);
  	if(y >= 0 && y < game.mapYS) {
  		if(x >= 0 && x < game.mapXS) {
        const c = game.mapData.charCodeAt(y * game.mapXS + x);
        if(c && c > 0) {
          return c;
        }
  		}
  	}
  }
  return 0;
}


function createHitmapForBitmapMap (game: Game) {
  if( hitmap[game.mapID] ) {
    return;  // Already created.
  }

  if(!getHitmapBitmap) {
    console.log('ERROR: getHitmapBitmap needs to be initialized.');
    return;
  }
  const bitmap = getHitmapBitmap(game.mapID);
  if(!bitmap) {
    console.log('ERROR: unable to obtain hitmap bitmap for map ' + game.mapID);
    return;
  }

  hitmap[game.mapID] = {
    xs: 512,
    ys: 512,
    spot: [],
    bitmap,
  };

  const scanner = function (color: number, xs: number, ys: number, xj: number, yj: number, spot: any) {
    for(let y = ys; y < 512; y += yj) {
      for(let x = xs; x < 512; x += xj) {
        if(bitmap[y * 512 + x] === color) {
          spot.push(Math.floor((y / 512) * WORLD_WIDTH) * WORLD_WIDTH +
                    Math.floor((x / 512) * WORLD_WIDTH));
        }
      }
    }
  };

  for(let z = 0; z <= game.zones; z++) {
    let spot: any = [];

    if( z > 0 ) {
      if( game.zoneKey.length > z ) {
        const color = game.zoneKey[z];

        scanner(color, 0, 0, 16, 16, spot);
        if( spot.length < MAX_POSITION_SPOTS ) {
          spot = [];  // Start again.
          scanner(color, 0, 0, 8, 8, spot);
          if( spot.length < MAX_POSITION_SPOTS ) {
            spot = [];  // Start again, now do the tighter stuff.
            scanner(color, 0, 0, 4, 4, spot);
            if( spot.length < MAX_POSITION_SPOTS ) {
              scanner(color, 0, 3, 1, 4, spot);
              if( spot.length < MAX_POSITION_SPOTS ) {
                scanner(color, 0, 2, 1, 4, spot);
                if( spot.length < MAX_POSITION_SPOTS ) {
                  scanner(color, 0, 1, 1, 4, spot);
                  if( spot.length < MAX_POSITION_SPOTS ) {
                    scanner(color, 0, 0, 1, 4, spot);
                    if( spot.length < MAX_POSITION_SPOTS ) {
                      spot = [];  // Start again, grab every single pixel.
                      scanner(color, 0, 0, 1, 1, spot);
                    }
                  }
                }
              }
            }
          }
        }
      }
      if(spot.length > MAX_POSITION_SPOTS) {
        spot.length = MAX_POSITION_SPOTS;
      }
    }
    hitmap[game.mapID].spot[z] = spot;
  }

}


function getNewZoneXYForHexmap (game: Game, zoneID: number) {
  var MapXS = game.mapXS;
  var MapYS = game.mapYS;
  var c = MapYS * MapXS;
  var offset = Math.floor(Math.random() * c);  // start scan at a random point.
  var skip = Math.floor(Math.random() * 8);    // 0 to 7, skip the first few finds.

  var HexPixelWidth = 48;   // Based on client map building hex shape (not quite even hex).
  var HexPixelHeight = 56;  // Based on client map building hex shape (not quite even hex).
  var TotalPixelWidth = Math.floor(MapXS * HexPixelWidth + HexPixelWidth / 2);
  var TotalPixelHeight = MapYS * (HexPixelHeight - 13) + 13;

  var zoneStr = zoneID.toString(16).toUpperCase();
  if( zoneStr.length < 2 )
      zoneStr = '0' + zoneStr;

  var index = -1;
  for(var i = 0; i < c; i++) {
      var p = (i + offset) % c;
      if( game.mapData.slice(p * 2, p * 2 + 2) === zoneStr ) {
          index = p;
          skip--;
          if( skip < 0 )  // do we need to find some more?
              break;
      }
  }

  if( index >= 0 ) {
      var hx = index % MapXS;
      var hy = Math.floor(index / MapXS);

      var px = hx * HexPixelWidth;
      if( (hy & 1) === 0 )  // Odd lines are indent lines.
          px = Math.floor(px + HexPixelWidth / 2);
      var py = hy * (HexPixelHeight - 13);  // Top and body of hex, not counting the bottom part.

      px = Math.floor(px + HexPixelWidth / 2);   // Move to center of hex.
      py = Math.floor(py + HexPixelHeight / 2);  // Move to center of hex.

      px += Math.floor(Math.random() * 29) - 14; // Random pixel within center of hex.
      py += Math.floor(Math.random() * 29) - 14; // Random pixel within center of hex.

      return {result: 0,
              x: Math.floor(px * WORLD_WIDTH / TotalPixelWidth),
              y: Math.floor(py * WORLD_WIDTH / TotalPixelHeight)
              };
  }
  return {result: 2, x: CENTER_POS, y: CENTER_POS};
}


/*
function zoneIDAtHexmapXY (game: Game, x: number, y: number) {
  // Returns zoneID on success, 0 for impassible or -1 on error.
  var HexSteps = [1, 3, 5, 7, 8, 10, 12, 14, 16, 17, 19, 21, 23];

  var MapXS = game.mapXS;
  var MapYS = game.mapYS;
  var HexPixelWidth = 48;   // Based on flash client map building hex shape (not quite even hex).
  var HexPixelHeight = 56;  // Based on flash client map building hex shape (not quite even hex).
  var TotalPixelWidth = Math.floor(MapXS * HexPixelWidth + HexPixelWidth / 2);
  var TotalPixelHeight = MapYS * (HexPixelHeight - 13) + 13;

  if( x < 0 || x >= WORLD_WIDTH || y < 0 || y >= WORLD_WIDTH )
      return -1;

  var px = Math.floor(x * TotalPixelWidth / WORLD_WIDTH);
  var py = Math.floor(y * TotalPixelHeight / WORLD_WIDTH);

  if( px < 0 )
      px = 0;
  if( px >= TotalPixelWidth )
      px = TotalPixelWidth - 1;
  if( py < 0 )
      py = 0;
  if( py >= TotalPixelHeight )
      py = TotalPixelHeight - 1;

  var hx = Math.floor(px / HexPixelWidth);
  var ix = px % HexPixelWidth;

  var hy = Math.floor(py / (HexPixelHeight - 13));
  var iy = py % (HexPixelHeight - 13);

  if( (hy & 1) === 0 ) {  // indent row
      if( ix < HexPixelWidth / 2 ) {   // left half
          if( iy < 13 ) {
              if( ix < HexSteps[iy] )
                  hx--;    // prev hex
              else hy--;   // above hex
          }
          else hx--;    // prev hex
      }
      else{     // right half
          if( iy < 13 ) {
              if( ix - HexPixelWidth / 2 < 24 - HexSteps[iy] )
                  hy--;  // above hex
              else {
                  // same hex
              }
          }
          else {
              // same hex
          }
      }
  }
  else {   // no indent row
      if( iy < 13 ) {
          if( ix < HexPixelWidth / 2 ) {
              if( ix < 24 - HexSteps[iy] ) {
                  hx--;
                  hy--;  // above left hex
              }
              else{
                  // same hex
              }
          }
          else {
              if( ix - HexPixelWidth / 2 >= HexSteps[iy] ) {
                  hy--;  // above right hex
              }
              else{
                  // same hex
              }
          }
      }
      else{
          // same hex
      }
  }

  if( hx < 0 ) {
      if( game.mapFlags & BF.MAP_FLAG_CAN_X_PAN_BOARD )
          hx = MapXS - 1;
      else return -1;  // off the map
  }
  if( hx >= MapXS ) {
      if( game.mapFlags & BF.MAP_FLAG_CAN_X_PAN_BOARD )
          hx = 0;
      else return -1 ;  // off the map
  }

  if( hy < 0 ) {
      if( game.mapFlags & BF.MAP_FLAG_CAN_Y_PAN_BOARD )
          hy = MapYS - 1;
      else return -1;  // off the map
  }
  if( hy >= MapYS ) {
      if( game.mapFlags & BF.MAP_FLAG_CAN_Y_PAN_BOARD )
          hy = 0;
      else return -1;  // off the map
  }

  var mi = (hy * MapXS + hx) * 2;
  var z = parseInt(game.mapData.slice(mi, mi + 2), 16);
  if(z && z > 0 && z <= game.zones) {
      return z;
  }
  return -1;
}
*/

function createHitmapForRawmapMap (game: Game) {
  if(hitmap[game.mapID]) {
    return;  // Already created.
  }

  // Calculate the zoneCoordinates for zone.

  const { mapXS, mapYS, mapData, zones } = game;
  const board: number[][] = [];
  for(let y = 0; y < mapYS; y++) {
    board[y] = [];
    for(let x = 0; x < mapXS; x++) {
      board[y].push(mapData.charCodeAt(y * mapXS + x));
    }
  }

  const zoneCoordinate: number[][] = [];
  for(let zone = 0; zone <= zones; zone++) {
    zoneCoordinate[zone] = [];
  }

  // Calculate positions:
  // Exclude edges and borders.
  for(let y = 1; y < mapYS - 1; y++) {
    for(let x = 1; x < mapXS - 1; x++) {
    	const zone = board[y][x];
    	if(zone) {
        let addCoordinate = true;
    		if(zone !== board[y - 1][x]) {
          addCoordinate = false;
        }
    		if(zone !== board[y][x - 1]) {
          addCoordinate = false;
        }
    		if(zone !== board[y + 1][x]) {
          addCoordinate = false;
    		}
    		if(zone !== board[y][x + 1]) {
          addCoordinate = false;
        }
        if(addCoordinate) { // Only add if not a bordered pixel, store as single int y*width+x.
          zoneCoordinate[zone].push(
            Math.floor(y / mapYS * BF.WORLD_HEIGHT) * BF.WORLD_WIDTH
            + Math.floor(x / mapXS * BF.WORLD_WIDTH));
        }
    	}
  	}
	}

  hitmap[game.mapID] = {
    xs: mapXS,
    ys: mapYS,
    spot: zoneCoordinate,
  };
}
